/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Bleed, Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { Conversation } from '../../../types';
import { useConversationModal } from '../../hooks/conversation-modal';

import messages from './messages';
import { useUpdateTime } from './use-update-time';

const styles = cssMap({
	wrapper: {
		display: 'flex',
		justifyContent: 'end',
		alignItems: 'center',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
	},
	wrapperSelected: {
		backgroundColor: token('color.background.selected'),
	},
	pressableChatItem: {
		borderRadius: token('radius.large'),
		backgroundColor: 'transparent',
		width: '100%',
		height: '100%',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	chatItemWrapper: {
		border: 'none',
		backgroundColor: 'transparent',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		width: '100%',
		height: '100%',
		position: 'relative',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	dropdownButton: {
		position: 'absolute',
	},
	buttonText: {
		maxWidth: '90%',
	},
	hidden: {
		opacity: '0',
	},
});

type ChatItemProps = {
	conversation: Conversation;
	isSelected: boolean;
	onSelectConversation: (conversationId: string) => void;
	isFullScreen: boolean;
};

export const ChatItem = ({
	conversation,
	isSelected,
	onSelectConversation,
	isFullScreen,
}: ChatItemProps): JSX.Element => {
	di(useConversationModal);

	const [isFocusedOrHovered, setIsFocusedOrHovered] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const lastMessageTime = useUpdateTime({ isoDate: conversation.lastMessageTimestamp });
	const { formatMessage } = useIntl();
	const { openModal } = useConversationModal();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleItemClick = useCallback(() => {
		onSelectConversation(conversation.id);
	}, [conversation.id, onSelectConversation]);

	const setIsHoveredTrue = useCallback(() => setIsFocusedOrHovered(true), []);
	const setIsHoveredFalse = useCallback(() => setIsFocusedOrHovered(false), []);
	const handleDropdownToggle = useCallback(
		(openMenuArgs: OnOpenChangeArgs) => setIsDropdownOpen(openMenuArgs.isOpen),
		[],
	);

	return (
		<Box
			xcss={styles.chatItemWrapper}
			onMouseEnter={setIsHoveredTrue}
			onMouseLeave={setIsHoveredFalse}
			onFocus={setIsHoveredTrue}
			onBlur={setIsHoveredFalse}
		>
			<Bleed inline="space.150" xcss={cx(styles.wrapper, isSelected && styles.wrapperSelected)}>
				<Pressable
					xcss={styles.pressableChatItem}
					onClick={(e, analyticsEvent) => {
						sendAnalyticsEvent(
							analyticsEvent.update({
								actionSubjectId: 'chatViewChatItemClick',
							}),
						);
						handleItemClick();
					}}
				>
					<Stack space="space.025" alignBlock="start" alignInline="start" xcss={styles.buttonText}>
						<Text align="start" weight={isSelected ? 'medium' : undefined}>
							{conversation.name}
						</Text>
						<Text size="small" color="color.text.subtlest">
							{lastMessageTime}
						</Text>
					</Stack>
				</Pressable>
				<Box
					xcss={cx(styles.dropdownButton, !isFocusedOrHovered && !isDropdownOpen && styles.hidden)}
				>
					<DropdownMenu<HTMLButtonElement>
						shouldRenderToParent
						onOpenChange={handleDropdownToggle}
						trigger={({ triggerRef, onClick, ...rest }) => (
							<IconButton
								{...rest}
								label={formatMessage(messages.chatOptionsForConversationButtonLabel, {
									conversationName: conversation.name,
								})}
								appearance="subtle"
								icon={MoreIcon}
								ref={triggerRef}
								onClick={(e, analyticsEvent) => {
									sendAnalyticsEvent(
										analyticsEvent.update({
											actionSubjectId: 'chatViewChatOptionsTrigger',
										}),
									);
									e.stopPropagation();

									onClick?.(e);
								}}
							/>
						)}
					>
						<DropdownItemGroup>
							<DropdownItem
								onClick={(e) => {
									e.stopPropagation();

									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'renameConversationButton',
									});

									openModal(conversation, 'edit');
								}}
							>
								{formatMessage(messages.chatRenameLabel)}
							</DropdownItem>
							<DropdownItem
								onClick={(e) => {
									e.stopPropagation();

									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'button',
										actionSubjectId: 'deleteConversationButton',
									});

									openModal(conversation, 'delete');
								}}
							>
								{formatMessage(messages.chatDeleteLabel)}
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				</Box>
			</Bleed>
		</Box>
	);
};
